import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ComboBox, Input, ListBox, Spinner, type Key } from 'src/design-system';
import { RootState } from '../../store';
import {
  setImpersonatedUser,
  clearImpersonation,
  syncImpersonationToStorage,
  clearImpersonationStorage
} from '../../store/user/userSlice';
import { searchAdminUsers, fetchInitialAdminUsers, AdminUser } from 'api/admin';
import { debounce } from 'lodash';

type UserOption = AdminUser & { isSelf?: boolean };

function formatUserLabel(u: UserOption) {
  if (u.isSelf) return 'Yourself';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return name ? `${u.email} (${name})` : (u.email ?? u.id);
}

const SEARCH_DEBOUNCE_MS = 300;

export function UserSwitcher() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const impersonatedUser = useSelector((state: RootState) => state.auth.impersonatedUser);
  const [searchResults, setSearchResults] = useState<AdminUser[]>([]);
  const initialListRef = useRef<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const runSearch = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSearchResults(initialListRef.current);
      return;
    }

    setIsLoading(true);
    try {
      const list = await searchAdminUsers(trimmed);
      setSearchResults(list);
    } catch (err) {
      console.error('Failed to search users:', err);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    fetchInitialAdminUsers()
      .then((list) => {
        if (!cancelled) {
          initialListRef.current = list;
          setSearchResults(list);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch initial users:', err);
          initialListRef.current = [];
          setSearchResults([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((query: string) => runSearch(query), SEARCH_DEBOUNCE_MS),
    [runSearch]
  );

  useEffect(() => {
    debouncedSearch(inputValue);
    return () => debouncedSearch.cancel();
  }, [inputValue, debouncedSearch]);

  const options = useMemo(() => {
    const selfOption: UserOption = {
      id: user?.id ?? '',
      email: user?.email ?? '',
      firstName: user?.firstName,
      lastName: user?.lastName,
      isSelf: true
    };

    const others: UserOption[] = searchResults
      .filter((u) => u.id !== user?.id)
      .map((u) => ({ ...u, isSelf: false }));

    const impersonatedAsOption: UserOption | null =
      impersonatedUser &&
      impersonatedUser.id !== user?.id &&
      !searchResults.some((u) => u.id === impersonatedUser.id)
        ? {
            id: impersonatedUser.id,
            email: impersonatedUser.email ?? '',
            firstName: impersonatedUser.firstName,
            lastName: impersonatedUser.lastName,
            isSelf: false
          }
        : null;

    return impersonatedAsOption
      ? [selfOption, impersonatedAsOption, ...others]
      : [selfOption, ...others];
  }, [user, impersonatedUser, searchResults]);

  const selectedKey = impersonatedUser?.id ?? null;

  const handleSelectionChange = (key: Key | null) => {
    if (key == null) {
      return;
    }

    const keyStr = String(key);

    if (keyStr === user?.id) {
      clearImpersonationStorage();
      dispatch(clearImpersonation());
      setTimeout(() => window.location.reload(), 50);
      return;
    }

    const selected =
      searchResults.find((u) => u.id === keyStr) ??
      (impersonatedUser?.id === keyStr
        ? {
            id: impersonatedUser.id,
            email: impersonatedUser.email ?? '',
            firstName: impersonatedUser.firstName,
            lastName: impersonatedUser.lastName
          }
        : null);

    if (selected) {
      const payload = {
        id: selected.id,
        email: selected.email,
        firstName: selected.firstName,
        lastName: selected.lastName
      };
      syncImpersonationToStorage(selected.id, payload);
      dispatch(setImpersonatedUser(payload));
      setTimeout(() => window.location.reload(), 50);
    }
  };

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-2">
      <ComboBox
        selectedKey={selectedKey}
        onSelectionChange={(key) => handleSelectionChange(key)}
        inputValue={inputValue}
        onInputChange={setInputValue}
        menuTrigger="focus"
        defaultFilter={() => true}
        className="w-64 min-w-0"
        aria-label="Impersonate user">
        <ComboBox.InputGroup>
          <Input placeholder="Impersonate by email or name" variant="secondary" />
          {isLoading ? <Spinner size="sm" /> : null}
          <ComboBox.Trigger />
        </ComboBox.InputGroup>
        <ComboBox.Popover>
          <ListBox>
            {options.map((option) => (
              <ListBox.Item key={option.id} id={option.id} textValue={formatUserLabel(option)}>
                <div className="flex flex-col gap-0.5">
                  <span className={option.isSelf ? 'font-semibold' : ''}>
                    {formatUserLabel(option)}
                  </span>
                  {!option.isSelf && option.discordUsername && (
                    <span className="text-xs text-default-400">
                      Discord: {option.discordUsername}
                    </span>
                  )}
                </div>
                <ListBox.ItemIndicator />
              </ListBox.Item>
            ))}
          </ListBox>
        </ComboBox.Popover>
      </ComboBox>
    </div>
  );
}
